import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Coins, User, ArrowUpRight, ArrowDownRight, Clock, CreditCard, Loader2, Sparkles } from "lucide-react";
import { useUserCredits } from "@/hooks/useUserCredits";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { CreditTransaction } from "@/hooks/useCompanyAccount";
import { DemoHint } from "@/components/demo/DemoHint";
import { purchasesEnabled, NATIVE_BILLING_NOTICE } from "@/lib/billing/purchaseGate";

const CREDIT_TIERS = [
  { tier: 1, price: 25, credits: 20, label: null },
  { tier: 2, price: 60, credits: 55, label: null },
  { tier: 3, price: 120, credits: 100, label: "Most Popular" },
  { tier: 4, price: 500, credits: 450, label: null },
];

interface Props {
  balance: number;
  transactions: CreditTransaction[];
}

export function CompanyCreditsTab({ balance, transactions }: Props) {
  const { balance: userBalance, loading: userLoading } = useUserCredits();
  const [purchasingTier, setPurchasingTier] = useState<number | null>(null);

  const handleBuyCredits = async (tier: number) => {
    if (!purchasesEnabled()) return;
    setPurchasingTier(tier);
    try {
      const { data, error } = await supabase.functions.invoke('create-credit-checkout', {
        body: { creditTier: tier },
      });

      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err: any) {
      console.error('Checkout error:', err);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setPurchasingTier(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Company Balance Card */}
      <Card className="p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Coins className="h-5 w-5 text-primary" />
          </div>
          <div>
            <DemoHint hintKey="section.companyCredits" side="right" showIndicator>
            <p className="text-sm text-muted-foreground">Company Credit Balance</p>
            </DemoHint>
            <p className="text-3xl font-bold text-foreground">{balance.toLocaleString()}</p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Shared credits available to all company members. Used first when performing paid actions like ATLAS queries and load boosts.
        </p>
      </Card>

      {/* Personal Balance Card */}
      <Card className="p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary">
            <User className="h-5 w-5 text-secondary-foreground" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Your Personal Credits</p>
            <p className="text-3xl font-bold text-foreground">{userLoading ? "…" : userBalance.toLocaleString()}</p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Credits you've purchased with your own payment method. Used as a fallback when company credits are insufficient.
        </p>
      </Card>

      {/* Buy Credits — hidden in the native app (no in-app purchase paths) */}
      {!purchasesEnabled() ? (
        <Card className="p-4 sm:p-6">
          <p className="text-xs text-muted-foreground">{NATIVE_BILLING_NOTICE}</p>
        </Card>
      ) : (
      <Card className="p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <CreditCard className="h-5 w-5 text-primary" />
          <h3 className="font-semibold text-foreground">Buy Personal Credits</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Purchase credits directly into your personal wallet. These are used as a fallback when company credits run out.
        </p>
        <div className="grid grid-cols-2 gap-3">
          {CREDIT_TIERS.map((t) => (
            <button
              key={t.tier}
              onClick={() => handleBuyCredits(t.tier)}
              disabled={purchasingTier !== null}
              className="relative flex flex-col items-center gap-1 rounded-lg border p-4 transition-all hover:border-primary hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              style={t.label ? { borderColor: "hsl(var(--primary))", boxShadow: "0 0 0 1px hsl(var(--primary) / 0.2)" } : undefined}
            >
              {t.label && (
                <Badge className="absolute -top-2.5 text-[9px] px-2 py-0.5 bg-primary text-primary-foreground">
                  <Sparkles className="h-2.5 w-2.5 mr-0.5" />
                  {t.label}
                </Badge>
              )}
              <span className="text-2xl font-bold text-foreground">{t.credits}</span>
              <span className="text-[10px] text-muted-foreground">credits</span>
              <span className="text-sm font-semibold text-primary mt-1">${t.price}</span>
              {purchasingTier === t.tier && (
                <Loader2 className="h-4 w-4 animate-spin text-primary absolute top-2 right-2" />
              )}
            </button>
          ))}
        </div>
      </Card>
      )}

      {/* Transaction History */}
      <Card className="p-4 sm:p-6">
        <h3 className="font-semibold text-foreground mb-4">Transaction History</h3>

        {transactions.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No transactions yet.</p>
        ) : (
          <div className="divide-y divide-border">
            {transactions.map(tx => {
              const isCredit = tx.amount > 0;
              const meta = (tx as any).metadata;
              const source = meta?.source === 'user_wallet' ? 'personal' : 'company';
              return (
                <div key={tx.id} className="flex items-center justify-between py-3 gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`flex h-8 w-8 items-center justify-center rounded-full flex-shrink-0 ${isCredit ? "bg-success-subtle" : "bg-danger-subtle"}`}>
                      {isCredit
                        ? <ArrowDownRight className="h-4 w-4 text-success" />
                        : <ArrowUpRight className="h-4 w-4 text-danger" />
                      }
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium text-foreground truncate">
                          {tx.description || tx.type.replace(/_/g, " ")}
                        </p>
                        <Badge variant="secondary" className="text-[9px] px-1 py-0">
                          {source}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {new Date(tx.created_at).toLocaleDateString()} {new Date(tx.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                  </div>

                  <div className="text-right flex-shrink-0">
                    <p className={`text-sm font-bold ${isCredit ? "text-success" : "text-danger"}`}>
                      {isCredit ? "+" : ""}{tx.amount}
                    </p>
                    <p className="text-[10px] text-muted-foreground">bal: {tx.balance_after}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
